import React from 'react';
import {BUNDLE_ICON} from '../../Constants';
import { useGetWorkspaceQuery } from '../../store/Api';
import { useGetProductBundleQuery } from '../../store/endpoints/ProductBundleEndpoints';
import { Callout, DirectionalHint, Icon, mergeStyleSets, Modal, ProgressIndicator, SearchBox, Spinner, SpinnerSize, Stack, Text, TextField, Toggle, useTheme} from '@fluentui/react';
import { useGetQuicksearchQuery } from '../../store/Api';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import useCan, { SEARCH } from '../../permissions/ability';
import { useGetStorefrontsQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useSearchSections } from './useSearchSections';
import {
    cascadeRemoveSugarToken,
    isCompleteSugarToken,
    normalizeForMatch,
    parseProductQuery,
    PSEUDONYM_ATTRIBUTES,
    quoteSugarValue,
    sugarChipLabel,
    tokenizeProductQuery,
} from './productQueryParser';
import { productQueryClasses } from './productQueryStyles';
import {
    taxonomyMatch,
    useAttributePills,
    useFacetIntel,
    useProductCompletion,
    useProductTaxonomy,
    useSearchPopularity,
} from './useProductQueryIntel';
import { EntityRequest, IGlobalSearchHit, IGlobalSearchContext} from './sections';

const DEBOUNCE_MS = 250;

interface IGlobalSearchPaletteProps {
    isOpen: boolean;
    onDismiss: () => void;
    /** Forwarded to CreateEntityHost via NavMenu — opens a create/details modal after the palette closes. */
    openEntity: (request: EntityRequest) => void;
    /** Where the palette was opened from; a workspace makes product rows offer an in-place "Add". */
    context?: IGlobalSearchContext;
    /** Start in the ">" product search mode instead of the general box. */
    initialMode?: 'products';
}

/** How long the pointer rests on a row before its overview flyout opens. */
const PREVIEW_HOVER_MS = 700;

const classes = {
    ...mergeStyleSets({
        results: { maxHeight: '55vh', overflowY: 'auto', padding: '4px 0' },
        sectionHeading: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px 2px',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: '#605e5c',
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75em',
            padding: '7px 16px',
            cursor: 'pointer',
            selectors: { '&:hover': { backgroundColor: '#f3f2f1' } },
        },
        rowActive: { backgroundColor: '#edebe9' },
        rowIcon: { flexShrink: 0, fontSize: 14, color: '#605e5c', width: 16, textAlign: 'center' },
        rowText: { minWidth: 0, flexGrow: 1 },
        label: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        secondary: { display: 'block', opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        empty: { padding: '14px 16px', opacity: 0.75 },
        hint: { padding: '14px 16px', opacity: 0.6 },
        filterPanel: { padding: '2px 14px 10px', borderBottom: '1px solid #edebe9' },
    }),
    // Chips, suggestion pill, token field, and filter-row styles are shared with the search
    // page's ProductSearchBox.
    ...productQueryClasses,
};

interface ISuggestionPill {
    /** Human field name shown on the pill ("Manufacturer"). */
    field: string;
    /** Sugar key the pill writes into the query ("mfr"). */
    sugarKey: string;
    value: string;
}

/**
 * The "search everything" command palette: a centered overlay opened from the header trigger or
 * Ctrl+K. Sections (actions, settings, storefronts, …) load independently — the instant client-side
 * sections render on every keystroke while server-hit sections follow the debounced term.
 */
const GlobalSearchPalette: React.FC<IGlobalSearchPaletteProps> = (props) => {
    /*
     * In passthrough mode the input is a token field: committed filters render as chips inside
     * the field (sugarChips) while `query` holds only the free text being typed. Outside
     * passthrough, `query` is simply the whole search box value. The composed string — sigil +
     * chips + free text — remains the single source of truth for parsing and dispatch, and
     * setComposed() is the one door back in: it re-parses any composed string and splits it into
     * chips (completed sugar) and free text (keywords and a pending `name=`), which keeps every
     * helper that used to write a raw query string working unchanged.
     */
    const [query, setQuery] = React.useState('');
    const [sugarChips, setSugarChips] = React.useState<string[]>([]);
    const [sigilActive, setSigilActive] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const inputWrapRef = React.useRef<HTMLDivElement>(null);
    const textMirrorRef = React.useRef<HTMLSpanElement>(null);
    const [typedTextWidth, setTypedTextWidth] = React.useState(0);
    const tokenInputRef = React.useRef<HTMLInputElement>(null);

    const composedQuery = React.useMemo(
        () => (sigilActive ? '> ' + [...sugarChips, query].filter((s) => s.length > 0).join(' ') : query),
        [sigilActive, sugarChips, query]
    );
    const debouncedQuery = useDebouncedValue(composedQuery, DEBOUNCE_MS);

    const term = composedQuery.trim().toLowerCase();
    const debouncedTerm = debouncedQuery.trim().toLowerCase();

    const setComposed = (composed: string) => {
        const p = parseProductQuery(composed);
        const pendingToken = p.sugarTokens.find((t) => /=$/.test(t));
        const chips = p.sugarTokens.filter((t) => !/=$/.test(t));
        setSigilActive(p.sigil || chips.length > 0);
        setSugarChips(chips);
        setQuery(
            [...p.keywordTokens, ...(pendingToken ? [pendingToken] : [])].join(' ') +
                (/\s$/.test(composed) && p.keywordTokens.length > 0 ? ' ' : '')
        );
    };

    // Stable identity: openEntity feeds the section hooks' useMemos — a fresh closure per render
    // would defeat their memoization and re-rank every section on every render.
    const { onDismiss, openEntity } = props;
    // In workspace context a product's details open OVER the palette rather than replacing it, so
    // closing the modal returns to the same search, chips, and "Added" marks.
    const keepOpenFor = (request: EntityRequest) => !!props.context && request.kind === 'product';
    const openEntityAndClose = React.useCallback((request: EntityRequest) => {
        if (!keepOpenFor(request)) onDismiss();
        openEntity(request);
    }, [onDismiss, openEntity, props.context]);

    // Workspace context: the name for the "Adding to …" chip, and which rows have already been added.
    const contextWorkspaceId = props.context?.kind === 'workspace' ? props.context.workspaceId : undefined;
    const contextBundleId = props.context?.kind === 'bundle' ? props.context.bundleId : undefined;
    const contextWorkspace = useGetWorkspaceQuery(contextWorkspaceId ?? '', { skip: !contextWorkspaceId || !props.isOpen });
    const contextBundle = useGetProductBundleQuery(contextBundleId ?? '', { skip: !contextBundleId || !props.isOpen });
    const contextName = props.context?.name ?? (contextWorkspaceId ? contextWorkspace.data?.name : contextBundle.data?.name);
    // Sections get the context with its name resolved, so rows and the details modal can label the add.
    const namedContext = React.useMemo<IGlobalSearchContext | undefined>(
        () => props.context ? { ...props.context, name: contextName } : undefined,
        [props.context, contextName]);
    const contextIsSet = !!contextWorkspaceId || !!contextBundleId;
    const [addedKeys, setAddedKeys] = React.useState<Set<string>>(new Set());
    // Hover flyout: rest on a row for a moment and its overview opens beside it.
    const [previewFor, setPreviewFor] = React.useState<{ hit: IGlobalSearchHit; target: HTMLElement } | undefined>();
    const previewTimer = React.useRef<number | undefined>(undefined);
    const cancelPreview = React.useCallback(() => {
        if (previewTimer.current !== undefined) window.clearTimeout(previewTimer.current);
        previewTimer.current = undefined;
        setPreviewFor(undefined);
    }, []);
    const armPreview = (hit: IGlobalSearchHit, target: HTMLElement) => {
        if (previewTimer.current !== undefined) window.clearTimeout(previewTimer.current);
        setPreviewFor(undefined);
        if (!hit.preview) return;
        previewTimer.current = window.setTimeout(() => setPreviewFor({ hit, target }), PREVIEW_HOVER_MS);
    };
    React.useEffect(() => { if (!props.isOpen) cancelPreview(); return cancelPreview; }, [props.isOpen, cancelPreview]);
    const [addingKey, setAddingKey] = React.useState<string>();
    const theme = useTheme();

    const { sections, isSearching } = useSearchSections({
        term,
        rawTerm: composedQuery.trim(),
        debouncedTerm,
        debouncedRawTerm: debouncedQuery.trim(),
        isOpen: props.isOpen,
        openEntity: openEntityAndClose, context: namedContext,
    });

    const flatHits = React.useMemo(() => sections.flatMap((s) => s.hits), [sections]);

    // Passthrough suggestion pills, two tiers. "As typed": the bare keywords become one-click
    // filters — literal text-to-sugar rewrites, instant, no validation (a wrong guess just finds
    // nothing). "Matches": quicksearch grounds the same keywords in real catalog values a moment
    // later, so a misspelling still surfaces the manufacturer/category the user meant.
    const parsed = React.useMemo(() => parseProductQuery(composedQuery.trim()), [composedQuery]);

    // Applied filters as removable chips. Labels come from re-parsing each token in isolation
    // (the same summaries the passthrough row shows). Cancelling a category cascades: its
    // subcategory goes with it, and attribute filters fall away once no category or subcategory
    // remains to define them.
    const chipLabel = sugarChipLabel;

    const removeSugarToken = (token: string) => rebuildSugar(cascadeRemoveSugarToken(sugarChips, token));

    // Validated "Matches" come from the org's real taxonomy lists — unpaged, RTK-cached, filtered
    // client-side, so "lapt" finds the Laptops category the moment the lists are warm. Quicksearch
    // (debounced) only supplies part numbers, which cannot be enumerated up front.
    const canSearch = useCan(SEARCH);
    // Not sigil-gated: arg-less getAll queries fetch once while the palette is open and stay
    // cached for the session — sigil-mode pills are instant on first use.
    const taxonomyEnabled = props.isOpen && canSearch;
    const { knownManufacturers, searchMeta, validCategories, validSubCategories } = useProductTaxonomy(taxonomyEnabled);

    const pills = React.useMemo((): ISuggestionPill[] => {
        if (!parsed.sigil || !parsed.keywords) return [];
        const value = parsed.keywordTokens.join(' ');
        // A taxonomy field that's already in the query doesn't get offered again. Category and
        // subcategory only offer when the typed text matches a value that HAS products (with the
        // real spelling) - an unmatched category filter strands the user on an empty result page.
        const category = parsed.criteria.category ? undefined : taxonomyMatch(validCategories, value);
        const subCategory = parsed.criteria.subCategory ? undefined : taxonomyMatch(validSubCategories, value);
        return [
            { field: 'Manufacturer', sugarKey: 'mfr', value },
            { field: 'Part#', sugarKey: 'mpn', value },
            ...(category ? [{ field: 'Category', sugarKey: 'cat', value: category }] : []),
            ...(subCategory ? [{ field: 'Subcategory', sugarKey: 'sub', value: subCategory }] : []),
        ];
    }, [parsed, validCategories, validSubCategories]);

    const debouncedParsed = React.useMemo(() => parseProductQuery(debouncedQuery.trim()), [debouncedQuery]);
    const debouncedKeywords = debouncedParsed.keywords;
    // Not sigil-gated: the products section has usually already fetched the same term into
    // the RTK cache, so this subscription costs nothing extra outside product mode.
    const validatedQuicksearch = useGetQuicksearchQuery(
        { value: debouncedKeywords },
        { skip: !props.isOpen || !canSearch || debouncedKeywords.length < 2 }
    );

    // Attribute suggestions: once a category (or subcategory) is picked the attribute space is
    // narrow enough to predict, so prefetch the search the passthrough row would run (one item of
    // payload) and offer its facets — attribute names first, then that attribute's top values.
    const selectedCatalog = useSelector((state: RootState) => state.search.selectedCatalog);
    const [selectedAttribute, setSelectedAttribute] = React.useState<string | undefined>();
    const facetPreviewEnabled = props.isOpen && canSearch && parsed.sigil
        && !!(debouncedParsed.criteria.category || debouncedParsed.criteria.subCategory || (debouncedParsed.criteria.manufacturer?.length ?? 0) > 0);
    // Attribute intelligence stays category-gated even though the preview also runs for a
    // picked brand (top-categories row) - cross-category attributes are noise.
    const { facetPreview, facets, aliasedFacetKeys } = useFacetIntel({
        enabled: facetPreviewEnabled,
        attributeGate: !!(debouncedParsed.criteria.category || debouncedParsed.criteria.subCategory),
        catalogName: selectedCatalog?.name,
        criteria: debouncedParsed.criteria,
    });
    // The preview scopes its manufacturer aggregation by any picked brands, which would collapse
    // the Top Brands row to the picked one (accidental single-select). Remember the unscoped list
    // per category - search accepts multiple manufacturers, so more brands stay on offer.
    const [topBrands, setTopBrands] = React.useState<{ scope: string, brands: string[] }>({ scope: '', brands: [] });
    React.useEffect(() => {
        const scope = `${debouncedParsed.criteria.category ?? ''}|${debouncedParsed.criteria.subCategory ?? ''}`;
        const unscoped = (debouncedParsed.criteria.manufacturer?.length ?? 0) === 0;
        const brands = facetPreview.data?.metadata?.manufacturers ?? [];
        if (facetPreviewEnabled && unscoped && brands.length > 0) {
            setTopBrands({ scope, brands });
        } else if (topBrands.scope !== scope && topBrands.brands.length > 0) {
            setTopBrands({ scope, brands: [] });
        }
    }, [facetPreview.data, facetPreviewEnabled, debouncedParsed.criteria.category, debouncedParsed.criteria.subCategory, debouncedParsed.criteria.manufacturer, topBrands.scope, topBrands.brands.length]);

    // Org search history (shared RTK entry with the Frequent Searches section) weights
    // completions: the brand or category people actually search wins the tie.
    const { orgSuggestions, popularityOf } = useSearchPopularity(props.isOpen && canSearch);
    // Search-log usage within the picked category ranks the visible attribute pills.
    const pickedCategory = debouncedParsed.criteria.category;
    const { allFacetNames, facetNames, pseudonymPills } = useAttributePills({
        enabled: props.isOpen && canSearch,
        category: pickedCategory,
        facets,
        aliasedFacetKeys,
    });
    const [morePickerOpen, setMorePickerOpen] = React.useState(false);
    const [moreFilter, setMoreFilter] = React.useState('');
    const moreButtonRef = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (!facetPreviewEnabled) {
            setSelectedAttribute(undefined);
            setMorePickerOpen(false);
        }
    }, [facetPreviewEnabled]);
    // A pending typed attribute ("Total Installed System Memory"= — or an alias like ram=)
    // opens its values row too.
    const shownAttribute = React.useMemo(() => {
        if (selectedAttribute) return selectedAttribute;
        if (!parsed.pendingAttribute) return undefined;
        const lower = parsed.pendingAttribute.toLowerCase();
        const exact = Object.keys(facetPreview.data?.metadata?.facets ?? {}).find((k) => k.toLowerCase() === lower);
        return exact ?? aliasedFacetKeys(parsed.pendingAttribute)[0];
    }, [selectedAttribute, parsed.pendingAttribute, facetPreview.data, aliasedFacetKeys]);

    // Values for the shown attribute: a real attribute reads its own facet list; a pseudonym
    // pill shows a normalized-deduped union across every attribute its alias reaches.
    const shownAttributeValues = React.useMemo(() => {
        if (!shownAttribute) return [];
        const pseudonym = PSEUDONYM_ATTRIBUTES.find((p) => p.label === shownAttribute);
        if (!pseudonym) return facets?.[shownAttribute] ?? [];
        const seen = new Set<string>();
        const union: string[] = [];
        for (const key of aliasedFacetKeys(pseudonym.alias)) {
            for (const v of facets?.[key] ?? []) {
                const norm = normalizeForMatch(v);
                if (!seen.has(norm)) {
                    seen.add(norm);
                    union.push(v);
                }
            }
        }
        return union;
    }, [shownAttribute, facets, aliasedFacetKeys]);

    const attributeToken = (name: string, value: string) => `${quoteSugarValue(name)}=${quoteSugarValue(value)}`;
    const hasAttributeToken = (name: string, value: string) =>
        parsed.sugarTokens.some((t) => t.toLowerCase() === attributeToken(name, value).toLowerCase());
    const toggleAttributeToken = (name: string, value: string) => {
        const token = attributeToken(name, value).toLowerCase();
        const adding = !hasAttributeToken(name, value);
        rebuildSugar(adding
            ? [...parsed.sugarTokens, attributeToken(name, value)]
            : parsed.sugarTokens.filter((t) => t.toLowerCase() !== token));
        if (adding) {
            // The pending `name=` (or a partial `alias=3`) that opened this values row has served
            // its purpose — leaving it in the input would send a half-typed attribute along with
            // the search. Drop input tokens whose attribute name resolves to the applied one.
            setQuery((prev) => tokenizeProductQuery(prev).filter((t) => {
                const m = t.match(/^("[^"]*"|[^\s="]+)=/);
                if (!m) return true;
                const tokenName = m[1].replace(/^"|"$/g, '');
                return tokenName.toLowerCase() !== name.toLowerCase()
                    && !aliasedFacetKeys(tokenName).includes(name);
            }).join(' '));
        }
    };

    const completion = useProductCompletion({
        parsed,
        query,
        facets,
        aliasedFacetKeys,
        quicksearchData: validatedQuicksearch.data,
        manufacturerItems: knownManufacturers.data?.items,
        validCategories,
        popularityOf,
    });

    const applyPill = (pill: ISuggestionPill) => {
        // The keywords become the filter value; the sugar chips already present are preserved.
        setSugarChips((chips) => [...chips, `${pill.sugarKey}:${quoteSugarValue(pill.value)}`]);
        setQuery('');
    };

    const acceptCompletion = () => {
        if (!completion) return;
        const tokens = tokenizeProductQuery(query.trim());
        const remaining = tokens.slice(0, -1);
        if (completion.trailing === '') {
            // A pending `"Name"=` stays in the input so the value can be typed onto it.
            setQuery([...remaining, completion.replacement].join(' '));
        } else {
            setSugarChips((chips) => [...chips, completion.replacement]);
            setQuery(remaining.join(' '));
        }
    };

    // The passthrough filter panel: an in-stock pill plus toggle-gated storefront/vendor pill
    // groups. Selecting a pill writes sugar tokens into the query — the query text stays the
    // source of truth, so a query already carrying storefront:/vendor: tokens auto-reveals its group.
    const [storefrontFilterOpen, setStorefrontFilterOpen] = React.useState(false);
    const [vendorFilterOpen, setVendorFilterOpen] = React.useState(false);
    const [priceFilterOpen, setPriceFilterOpen] = React.useState(false);
    const showStorefrontPills = storefrontFilterOpen || parsed.storefrontNames.length > 0;
    const showVendorPills = vendorFilterOpen || (parsed.criteria.vendor?.length ?? 0) > 0;
    const showPriceFields = priceFilterOpen || parsed.criteria.lowPrice !== undefined || parsed.criteria.highPrice !== undefined;
    const filterStorefronts = useGetStorefrontsQuery(undefined, { skip: !props.isOpen || !parsed.sigil || !showStorefrontPills });

    React.useEffect(() => {
        if (props.isOpen) {
            setStorefrontFilterOpen(false);
            setVendorFilterOpen(false);
            setPriceFilterOpen(false);
        }
    }, [props.isOpen]);

    /** Turning a group's toggle off also clears its tokens, or the auto-reveal would pin it open. */
    const setFilterGroup = (group: 'storefront' | 'vendor' | 'price', on: boolean) => {
        const setOpen = group === 'storefront' ? setStorefrontFilterOpen
            : group === 'vendor' ? setVendorFilterOpen
            : setPriceFilterOpen;
        setOpen(on);
        if (!on) {
            const pattern = group === 'storefront' ? /^(storefront|store):/i
                : group === 'vendor' ? /^vendor:/i
                : /^price:/i;
            rebuildSugar(parsed.sugarTokens.filter((t) => !pattern.test(t)));
        }
    };

    /** Rewrites the price: token from the min/max fields; both empty removes it. */
    const setPriceRange = (low: number | undefined, high: number | undefined) => {
        const rest = parsed.sugarTokens.filter((t) => !/^price:/i.test(t));
        const token = low !== undefined && high !== undefined ? `price:${low}-${high}`
            : low !== undefined ? `price:${low}-`
            : high !== undefined ? `price:-${high}`
            : undefined;
        rebuildSugar(token ? [...rest, token] : rest);
    };

    const parsePriceField = (value?: string): number | undefined => {
        const n = parseFloat(value ?? '');
        return isNaN(n) ? undefined : n;
    };

    const rebuildSugar = (sugarTokens: string[]) => {
        setSigilActive(true);
        setSugarChips(sugarTokens.filter((t) => !/=$/.test(t)));
        // sugarTokens comes from the parsed COMPOSED query, so any complete sugar still sitting
        // in the free text (a raw processor=i7 the user declined to Tab-complete) is now a chip.
        // Strip it from the text or it rides along twice; keywords and pending fragments stay.
        setQuery((prev) => tokenizeProductQuery(prev).filter((t) => !isCompleteSugarToken(t)).join(' '));
    };
    const keyedToken = (key: string, value: string) => `${key}:${quoteSugarValue(value)}`;
    const hasKeyedToken = (key: string, value: string) =>
        parsed.sugarTokens.some((t) => t.toLowerCase() === keyedToken(key, value).toLowerCase());
    const toggleKeyedToken = (key: string, value: string) => {
        const token = keyedToken(key, value).toLowerCase();
        rebuildSugar(hasKeyedToken(key, value)
            ? parsed.sugarTokens.filter((t) => t.toLowerCase() !== token)
            : [...parsed.sugarTokens, keyedToken(key, value)]);
    };

    React.useEffect(() => setActiveIndex(0), [flatHits.length]);
    // A fresh palette per open: stale queries re-running their server hits on reopen would be noise.
    // A 'products' open lands straight in the ">" token field.
    React.useEffect(() => {
        if (props.isOpen) {
            setQuery('');
            setSugarChips([]);
            setSigilActive(props.initialMode === 'products');
            setAddedKeys(new Set());
            setAddingKey(undefined);
        }
    }, [props.isOpen, props.initialMode]);

    React.useLayoutEffect(() => {
        setTypedTextWidth(textMirrorRef.current?.offsetWidth ?? 0);
    }, [query]);

    const runHit = (hit: IGlobalSearchHit) => {
        if (!hit.keepOpen) props.onDismiss();
        hit.run();
    };

    /** The trailing "Add" action: stays in the palette, marks the row done on success. */
    const runSecondary = async (hit: IGlobalSearchHit) => {
        if (!hit.secondaryRun) return;
        const key = `${hit.section}-${hit.key}`;
        if (addedKeys.has(key) || addingKey === key) return;
        setAddingKey(key);
        try {
            const ok = await hit.secondaryRun();
            if (ok) setAddedKeys((prev) => new Set(prev).add(key));
        } finally {
            setAddingKey(undefined);
        }
    };

    /** Enter — Fluent's SearchBox intercepts it before onKeyDown and calls onSearch instead. */
    const onSearch = () => {
        const hit = flatHits[activeIndex] ?? flatHits[0];
        if (hit) runHit(hit);
    };

    const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        if (ev.key === 'Tab' && completion) {
            ev.preventDefault();
            acceptCompletion();
            return;
        }
        if (flatHits.length === 0) return;
        switch (ev.key) {
            case 'ArrowDown':
                ev.preventDefault();
                setActiveIndex((i) => (i + 1) % flatHits.length);
                break;
            case 'ArrowUp':
                ev.preventDefault();
                setActiveIndex((i) => (i - 1 + flatHits.length) % flatHits.length);
                break;
        }
    };

    // Flat keyboard index across section boundaries: row N's absolute position is its section's
    // offset plus its index within the section.
    let hitOffset = 0;

    return (
        <Modal
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            styles={{
                root: { alignItems: 'flex-start' },
                main: { marginTop: '10vh', width: 560, maxWidth: '92vw', minHeight: 0, borderRadius: 4 },
            }}
        >
            <div style={{ position: 'relative' }} ref={inputWrapRef}>
                {!sigilActive ? (
                    <SearchBox
                        placeholder='Search settings, storefronts, contacts, orders, products…'
                        ariaLabel='Search query'
                        value={query}
                        onChange={(_, value) => {
                            const next = value ?? '';
                            // ">" flips into the passthrough token field; setComposed also
                            // promotes any complete sugar in a pasted/filled string to chips.
                            if (next.startsWith('>')) setComposed(next);
                            else setQuery(next);
                        }}
                        onSearch={onSearch}
                        onKeyDown={onKeyDown}
                        iconProps={{ iconName: 'Search' }}
                        styles={{ root: { margin: '12px 12px 8px', height: 36 } }}
                    />
                ) : (
                    <div className={classes.tokenField} style={{ margin: '12px 12px 8px' }} onClick={() => tokenInputRef.current?.focus()}>
                        <span className={classes.sigilGlyph}>{'>'}</span>
                        {contextIsSet && (
                            <span
                                title={`Products you add here go to this ${contextBundleId ? 'bundle' : 'workspace'}. Enter opens a product, Shift+Enter adds it.`}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '2px 8px',
                                    borderRadius: 12, whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
                                    color: theme.palette.themeDarker, backgroundColor: theme.palette.themeLighter
                                }}
                            >
                                <Icon iconName={contextBundleId ? BUNDLE_ICON : 'WorkItem'} styles={{ root: { fontSize: 10 } }} />
                                Adding to {contextName ?? (contextBundleId ? 'bundle' : 'workspace')}
                            </span>
                        )}
                        {sugarChips.map((token, index) => (
                            <span
                                key={`${token}-${index}`}
                                className={`${classes.pill} ${classes.pillOn}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'default' }}
                            >
                                {chipLabel(token)}
                                <button
                                    type='button'
                                    aria-label={`Remove ${chipLabel(token)}`}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        removeSugarToken(token);
                                    }}
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', display: 'inline-flex' }}
                                >
                                    <Icon iconName='Cancel' styles={{ root: { fontSize: 9 } }} />
                                </button>
                            </span>
                        ))}
                        <input
                            ref={tokenInputRef}
                            className={classes.tokenInput}
                            aria-label='Search query'
                            placeholder={sugarChips.length === 0 ? 'Search products…' : ''}
                            value={query}
                            style={completion ? { flexGrow: 0, minWidth: 20, width: Math.max(typedTextWidth + 12, 20) } : undefined}
                            autoFocus
                            onChange={(ev) => {
                                const next = ev.target.value;
                                // Space commits: completed sugar tokens promote out of the free
                                // text into chips; the token still being typed stays put. An "="
                                // suspends promotion — the whole text may be one spaced attribute
                                // name ("Total Installed System Memory=32 GB").
                                if (/\s$/.test(next) && !next.includes('=')) {
                                    const tokens = tokenizeProductQuery(next);
                                    const promoted = tokens.filter(isCompleteSugarToken);
                                    if (promoted.length > 0) {
                                        setSugarChips((chips) => [...chips, ...promoted]);
                                        setQuery(tokens.filter((t) => !isCompleteSugarToken(t)).join(' ') + ' ');
                                        return;
                                    }
                                }
                                setQuery(next);
                            }}
                            onKeyDown={(ev) => {
                                if (ev.key === 'Enter') {
                                    ev.preventDefault();
                                    const active = flatHits[activeIndex] ?? flatHits[0];
                                    if (ev.shiftKey && active?.secondaryRun) void runSecondary(active);
                                    else onSearch();
                                    return;
                                }
                                if (ev.key === 'Backspace' && query === '') {
                                    ev.preventDefault();
                                    if (sugarChips.length > 0) {
                                        // Backing into a chip re-opens it for editing instead of
                                        // destroying it - the committed text returns to the input
                                        // so you can keep typing from where you were.
                                        const last = sugarChips[sugarChips.length - 1];
                                        setSugarChips((chips) => chips.slice(0, -1));
                                        setQuery(last);
                                    } else {
                                        setSigilActive(false);
                                    }
                                    return;
                                }
                                onKeyDown(ev);
                            }}
                        />
                        {completion && (
                            <button
                                type='button'
                                className={classes.suggestionPill}
                                onMouseDown={(ev) => ev.preventDefault()}
                                onClick={acceptCompletion}
                            >
                                <span className={classes.suggestionTabChip}>TAB {'⇥'}</span>
                                <span className={classes.suggestionValue}>{completion.value}</span>
                                <span className={classes.suggestionField}>· {completion.field}</span>
                            </button>
                        )}
                        <span ref={textMirrorRef} className={classes.textMirror} aria-hidden>{query}</span>
                    </div>
                )}
                {isSearching && (
                    <ProgressIndicator
                        barHeight={2}
                        styles={{ root: { position: 'absolute', left: 12, right: 12, bottom: 0 }, itemProgress: { padding: 0 } }}
                    />
                )}
            </div>
            {pills.length > 0 && (
                <div className={classes.pillRow}>
                    {pills.map((pill) => (
                        <button
                            key={`${pill.sugarKey}-${pill.value}`}
                            type='button'
                            className={classes.pill}
                            style={{ font: 'inherit' }}
                            onMouseDown={(ev) => ev.preventDefault()}
                            onClick={() => applyPill(pill)}
                        >
                            <span className={classes.pillField}>{pill.field}:</span> {pill.value}
                        </button>
                    ))}
                </div>
            )}
            {parsed.sigil && (
                <div className={classes.filterPanel} onMouseDown={(ev) => ev.preventDefault()}>
                    {parsed.sugarTokens.length === 0 && !query.trim() && ((orgSuggestions.data?.categories?.length ?? 0) > 0 || (orgSuggestions.data?.manufacturers?.length ?? 0) > 0) && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>Frequent</span>
                            <div className={classes.filterOptions}>
                                {(orgSuggestions.data?.categories ?? []).slice(0, 4).map((t) => (
                                    <button
                                        key={`freq-cat-${t.value}`}
                                        type='button'
                                        className={classes.pill}
                                        style={{ font: 'inherit' }}
                                        title={`Frequently searched category (${t.count} recent searches)`}
                                        onClick={() => toggleKeyedToken('cat', t.value)}
                                    >
                                        {t.value}
                                    </button>
                                ))}
                                {(orgSuggestions.data?.manufacturers ?? []).slice(0, 4).map((t) => (
                                    <button
                                        key={`freq-mfr-${t.value}`}
                                        type='button'
                                        className={classes.pill}
                                        style={{ font: 'inherit', fontStyle: 'italic' }}
                                        title={`Frequently searched brand (${t.count} recent searches)`}
                                        onClick={() => toggleKeyedToken('mfr', t.value)}
                                    >
                                        {t.value}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className={classes.filterGroupLabel} style={{ paddingTop: 6, width: 'auto' }}>Filter by</div>
                    <div className={classes.filterOptions} style={{ alignItems: 'center', maxHeight: 'none' }}>
                        <Toggle
                            label='In Stock'
                            inlineLabel
                            checked={parsed.criteria.inStock !== false}
                            onChange={() => {
                                // Default is in-stock, so ON needs no token and OFF is `anystock`.
                                const rest = parsed.sugarTokens.filter((t) =>
                                    !['instock', 'in-stock', 'anystock', 'any-stock'].includes(t.toLowerCase()));
                                rebuildSugar(parsed.criteria.inStock !== false ? [...rest, 'anystock'] : rest);
                            }}
                            styles={{ root: { marginBottom: 0 }, label: { fontSize: 11, padding: 0, marginRight: 2 } }}
                        />
                        <Toggle
                            label='Storefront'
                            inlineLabel
                            checked={showStorefrontPills}
                            onChange={(_, checked) => setFilterGroup('storefront', checked === true)}
                            styles={{ root: { marginBottom: 0, marginLeft: 12 }, label: { fontSize: 11, padding: 0, marginRight: 2 } }}
                        />
                        <Toggle
                            label='Vendor'
                            inlineLabel
                            checked={showVendorPills}
                            onChange={(_, checked) => setFilterGroup('vendor', checked === true)}
                            styles={{ root: { marginBottom: 0, marginLeft: 12 }, label: { fontSize: 11, padding: 0, marginRight: 2 } }}
                        />
                        <Toggle
                            label='Price'
                            inlineLabel
                            checked={showPriceFields}
                            onChange={(_, checked) => setFilterGroup('price', checked === true)}
                            styles={{ root: { marginBottom: 0, marginLeft: 12 }, label: { fontSize: 11, padding: 0, marginRight: 2 } }}
                        />
                    </div>
                    {showPriceFields && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>Price</span>
                            {/* stopPropagation: the panel's mousedown preventDefault protects pill
                                clicks from stealing input focus, but these fields need real focus. */}
                            <div className={classes.filterOptions} onMouseDown={(ev) => ev.stopPropagation()}>
                                <TextField
                                    label='Minimum Price'
                                    type='number'
                                    prefix='$'
                                    value={parsed.criteria.lowPrice?.toString() ?? ''}
                                    onChange={(_, v) => setPriceRange(parsePriceField(v), parsed.criteria.highPrice)}
                                    onKeyDown={(ev) => {
                                        // Enter finalizes the search from here, same as in the main input.
                                        if (ev.key === 'Enter') {
                                            ev.preventDefault();
                                            onSearch();
                                        }
                                    }}
                                    styles={{ root: { width: 140 }, subComponentStyles: { label: { root: { fontSize: 11, paddingTop: 0 } } } }}
                                />
                                <TextField
                                    label='Maximum Price'
                                    type='number'
                                    prefix='$'
                                    value={parsed.criteria.highPrice?.toString() ?? ''}
                                    onChange={(_, v) => setPriceRange(parsed.criteria.lowPrice, parsePriceField(v))}
                                    onKeyDown={(ev) => {
                                        if (ev.key === 'Enter') {
                                            ev.preventDefault();
                                            onSearch();
                                        }
                                    }}
                                    styles={{ root: { width: 140, marginLeft: 10 }, subComponentStyles: { label: { root: { fontSize: 11, paddingTop: 0 } } } }}
                                />
                            </div>
                        </div>
                    )}
                    {facetPreviewEnabled && !!(parsed.criteria.category || parsed.criteria.subCategory)
                        && topBrands.brands.length > 0 && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>Top Brands</span>
                            <div className={classes.filterOptions}>
                                {topBrands.brands.map((name) => (
                                    <button
                                        key={`topbrand-${name}`}
                                        type='button'
                                        className={`${classes.pill} ${hasKeyedToken('mfr', name) ? classes.pillOn : ''}`}
                                        style={{ font: 'inherit' }}
                                        onClick={() => toggleKeyedToken('mfr', name)}
                                    >
                                        {name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {facetPreviewEnabled && !(parsed.criteria.category || parsed.criteria.subCategory)
                        && (parsed.criteria.manufacturer?.length ?? 0) > 0 && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>Top Categories</span>
                            <div className={classes.filterOptions}>
                                {Object.values(facetPreview.data?.metadata?.segments ?? {}).flat()
                                    .filter((c, i, arr) => !!c && arr.indexOf(c) === i)
                                    .slice(0, 10)
                                    .map((name) => (
                                        <button
                                            key={`topcat-${name}`}
                                            type='button'
                                            className={`${classes.pill} ${hasKeyedToken('cat', name) ? classes.pillOn : ''}`}
                                            style={{ font: 'inherit' }}
                                            onClick={() => toggleKeyedToken('cat', name)}
                                        >
                                            {name}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}
                    {facetPreviewEnabled && (facetNames.length > 0 || facetPreview.isFetching) && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>Attributes</span>
                            <div className={classes.filterOptions}>
                                {pseudonymPills.map((p) => (
                                    <button
                                        key={`pseudo-${p.label}`}
                                        type='button'
                                        className={`${classes.pill} ${shownAttribute === p.label ? classes.pillOn : ''}`}
                                        style={{ font: 'inherit', fontStyle: 'italic' }}
                                        onClick={() => setSelectedAttribute(selectedAttribute === p.label ? undefined : p.label)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                                {facetNames.map((name) => (
                                    <button
                                        key={name}
                                        type='button'
                                        className={`${classes.pill} ${shownAttribute === name ? classes.pillOn : ''}`}
                                        style={{ font: 'inherit' }}
                                        onClick={() => setSelectedAttribute(selectedAttribute === name ? undefined : name)}
                                    >
                                        {name}
                                    </button>
                                ))}
                                {allFacetNames.length > facetNames.length && (
                                    <button
                                        ref={moreButtonRef}
                                        type='button'
                                        className={classes.pill}
                                        style={{ font: 'inherit', fontStyle: 'italic' }}
                                        onClick={() => setMorePickerOpen(!morePickerOpen)}
                                    >
                                        +{allFacetNames.length - facetNames.length} more…
                                    </button>
                                )}
                                {facetPreview.isFetching && <Spinner size={SpinnerSize.xSmall} />}
                            </div>
                        </div>
                    )}
                    {morePickerOpen && (
                        <Callout
                            target={moreButtonRef.current}
                            directionalHint={DirectionalHint.bottomLeftEdge}
                            isBeakVisible={false}
                            gapSpace={4}
                            onDismiss={() => setMorePickerOpen(false)}
                            setInitialFocus
                            styles={{ calloutMain: { padding: 8, width: 280 } }}
                        >
                            <SearchBox
                                placeholder='Filter attributes…'
                                value={moreFilter}
                                onChange={(_, v) => setMoreFilter(v ?? '')}
                                underlined
                            />
                            <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {allFacetNames
                                    .filter((name) => !moreFilter.trim() || name.toLowerCase().includes(moreFilter.trim().toLowerCase()))
                                    .map((name) => (
                                        <button
                                            key={name}
                                            type='button'
                                            className={classes.pill}
                                            style={{ font: 'inherit', textAlign: 'left', borderRadius: 3 }}
                                            onClick={() => {
                                                setSelectedAttribute(name);
                                                setMorePickerOpen(false);
                                                setMoreFilter('');
                                            }}
                                        >
                                            {name}
                                        </button>
                                    ))}
                            </div>
                        </Callout>
                    )}
                    {facetPreviewEnabled && shownAttribute && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>{shownAttribute}</span>
                            <div className={classes.filterOptions}>
                                {shownAttributeValues.slice(0, 8).map((value) => (
                                    <button
                                        key={value}
                                        type='button'
                                        className={`${classes.pill} ${hasAttributeToken(shownAttribute, value) ? classes.pillOn : ''}`}
                                        style={{ font: 'inherit' }}
                                        onClick={() => toggleAttributeToken(shownAttribute, value)}
                                    >
                                        {value}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {showStorefrontPills && (filterStorefronts.data?.items?.length ?? 0) > 0 && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>Storefronts</span>
                            <div className={classes.filterOptions}>
                                {(filterStorefronts.data?.items ?? []).map((sf) => {
                                    const name = sf.name || sf.subUrl || sf.id || '';
                                    return (
                                        <button
                                            key={sf.id}
                                            type='button'
                                            className={`${classes.pill} ${hasKeyedToken('storefront', name) ? classes.pillOn : ''}`}
                                            style={{ font: 'inherit' }}
                                            onClick={() => toggleKeyedToken('storefront', name)}
                                        >
                                            {name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {showVendorPills && (searchMeta.data?.vendors?.length ?? 0) > 0 && (
                        <div className={classes.filterGroupRow}>
                            <span className={classes.filterGroupLabel}>Vendors</span>
                            <div className={classes.filterOptions}>
                                {(searchMeta.data?.vendors ?? []).map((vendor) => (
                                    <button
                                        key={vendor}
                                        type='button'
                                        className={`${classes.pill} ${hasKeyedToken('vendor', vendor) ? classes.pillOn : ''}`}
                                        style={{ font: 'inherit' }}
                                        onClick={() => toggleKeyedToken('vendor', vendor)}
                                    >
                                        {vendor}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className={classes.results} role='listbox'>
                {term === '' && (
                    <Text className={classes.hint} block>
                        Type to find anything, or pick an action below. Start with {'>'} for a
                        product search with filters, like {'>'} laptop cat:Laptops mfr:Lenovo ram=16GB
                    </Text>
                )}
                {term !== '' && debouncedTerm === term && !isSearching && sections.length === 0 && (
                    <Text className={classes.empty} block>No results.</Text>
                )}
                {previewFor?.hit.preview && (
                    <Callout
                        target={previewFor.target}
                        directionalHint={DirectionalHint.rightCenter}
                        isBeakVisible
                        gapSpace={6}
                        setInitialFocus={false}
                        doNotLayer={false}
                        onDismiss={cancelPreview}
                        styles={{ root: { pointerEvents: 'none', maxWidth: 340 }, calloutMain: { padding: 12 } }}
                    >
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                            {previewFor.hit.preview.image && (
                                <img
                                    src={previewFor.hit.preview.image}
                                    alt=''
                                    style={{ width: 72, height: 72, objectFit: 'contain', flexShrink: 0, background: theme.palette.white, borderRadius: 4 }}
                                />
                            )}
                            <div style={{ minWidth: 0 }}>
                                <Text variant='mediumPlus' block styles={{ root: { fontWeight: 600, lineHeight: '20px' } }}>{previewFor.hit.preview.title}</Text>
                                {previewFor.hit.preview.lines.map((line, i) => (
                                    <Text key={i} variant='small' block styles={{ root: { color: theme.palette.neutralSecondary, lineHeight: '18px' } }}>{line}</Text>
                                ))}
                                {previewFor.hit.preview.description && (
                                    <Text variant='small' block styles={{ root: { marginTop: 6, lineHeight: '18px' } }}>{previewFor.hit.preview.description}</Text>
                                )}
                            </div>
                        </div>
                    </Callout>
                )}
                {sections.map((section) => {
                    const sectionOffset = hitOffset;
                    hitOffset += section.hits.length;
                    return (
                        <div key={section.section}>
                            <div className={classes.sectionHeading}>
                                <span>{section.label}</span>
                                {section.isLoading && <Spinner size={SpinnerSize.xSmall} />}
                            </div>
                            {section.hits.map((hit, index) => {
                                const absoluteIndex = sectionOffset + index;
                                return (
                                    <div
                                        key={`${hit.section}-${hit.key}`}
                                        role='option'
                                        aria-selected={absoluteIndex === activeIndex}
                                        className={`${classes.row} ${absoluteIndex === activeIndex ? classes.rowActive : ''}`}
                                        onMouseEnter={(ev) => { setActiveIndex(absoluteIndex); armPreview(hit, ev.currentTarget); }}
                                        onMouseLeave={cancelPreview}
                                        onMouseDown={(ev) => ev.preventDefault()}
                                        onClick={() => { cancelPreview(); runHit(hit); }}
                                    >
                                        {hit.icon && <Icon iconName={hit.icon} className={classes.rowIcon} />}
                                        <Stack className={classes.rowText}>
                                            <Text className={classes.label}>{hit.label}</Text>
                                            {hit.secondary && (
                                                <Text variant='small' className={classes.secondary}>{hit.secondary}</Text>
                                            )}
                                        </Stack>
                                        {hit.secondaryRun && (() => {
                                            const key = `${hit.section}-${hit.key}`;
                                            const done = addedKeys.has(key);
                                            const busy = addingKey === key;
                                            return (
                                                <button
                                                    type='button'
                                                    aria-label={`${done ? (hit.secondaryDoneLabel ?? 'Done') : (hit.secondaryLabel ?? 'Add')}: ${hit.label}`}
                                                    disabled={done || busy}
                                                    onMouseDown={(ev) => ev.preventDefault()}
                                                    onClick={(ev) => { ev.stopPropagation(); void runSecondary(hit); }}
                                                    style={{
                                                        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4,
                                                        fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 12,
                                                        cursor: done || busy ? 'default' : 'pointer',
                                                        border: `1px solid ${done ? theme.palette.neutralTertiary : theme.palette.themePrimary}`,
                                                        color: done ? theme.palette.neutralSecondary : theme.palette.white,
                                                        backgroundColor: done ? 'transparent' : theme.palette.themePrimary,
                                                        opacity: busy ? 0.7 : 1
                                                    }}
                                                >
                                                    {busy
                                                        ? <Spinner size={SpinnerSize.xSmall} />
                                                        : <Icon iconName={done ? 'CheckMark' : 'Add'} styles={{ root: { fontSize: 10 } }} />}
                                                    {done ? (hit.secondaryDoneLabel ?? 'Added') : (hit.secondaryLabel ?? 'Add')}
                                                </button>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

export default GlobalSearchPalette;
